import { useLayoutEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'

import { useOptionalAuth } from '../../hooks/useAuth'
import { getGroupTabs } from './navigation'

/**
 * The phone's group tabs — VH-297 (HP-38 rule 5, LifeBrain's ModuleHeader)
 *
 * With the drawer gone, a group's sub-pages (Workouts → Training Locations,
 * Exercises …) are a tab strip at the top of each of its pages. Each tab is a
 * link: VitaHub's sub-sections are pages of their own, so the addresses stay.
 * Read from the one navigation list, so a new sub-page gets its tab there.
 *
 * Phone only (Reto 2026-10-23): from 768px the sidebar groups show the same.
 * The strip scrolls sideways inside itself, and the current tab is brought
 * into view.
 */
export default function GroupTabs() {
  const { t } = useTranslation()
  const { user } = useOptionalAuth()
  const { pathname } = useLocation()
  const scroller = useRef(null)
  const found = getGroupTabs(t, user, pathname)

  // scrollLeft rather than scrollIntoView, which would also scroll the page.
  useLayoutEffect(() => {
    const el = scroller.current
    const tab = el?.querySelector('[aria-current="page"]')
    if (!el || !tab) return
    const start = tab.offsetLeft - el.offsetLeft
    const end = start + tab.offsetWidth
    if (start < el.scrollLeft) el.scrollLeft = Math.max(0, start - 16)
    else if (end > el.scrollLeft + el.clientWidth) el.scrollLeft = end - el.clientWidth + 16
  }, [pathname])

  if (!found) return null
  const { group, tabs, current } = found

  return (
    <nav
      ref={scroller}
      aria-label={t('nav.sections', { name: group.name })}
      className="md:hidden flex gap-1 overflow-x-auto border-b border-outline-variant bg-surface-container px-2"
    >
      {tabs.map((tab) => {
        const active = tab.key === current
        return (
          <Link
            key={tab.key}
            to={tab.href}
            aria-current={active ? 'page' : undefined}
            className={clsx(
              'flex min-h-[44px] shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-3 text-sm font-medium',
              active
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-900'
            )}
          >
            <tab.icon className="h-5 w-5" />
            {tab.name}
          </Link>
        )
      })}
    </nav>
  )
}
